'use client';

import React, { useMemo, useState } from 'react';
import { ExtendedArchiMateDiagram, Change } from '@/lib/archimate/extended-types';
import {
  addChangelogEntry,
  getChangelogByType,
  getChangelogByUser,
  getChangelogSince,
  generateChangelogMarkdown,
  exportChangelogToCSV,
} from '@/lib/archimate/changelog-utils';
import { History, Filter, Download, FileText, X, Clock, User, Tag } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface ChangelogPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onSelectElement: (elementId: string) => void;
}

export default function ChangelogPanel({
  diagram,
  onUpdate,
  onClose,
  onSelectElement,
}: ChangelogPanelProps) {
  const [viewMode, setViewMode] = useState<'timeline' | 'filters' | 'stats'>('timeline');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [filterDays, setFilterDays] = useState<number>(30);
  const [selectedEntry, setSelectedEntry] = useState<Change | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  // Get filtered changelog (returns individual changes, not entries)
  const filteredEntries = useMemo((): Change[] => {
    // First, flatten all changes from all entries
    let changes = diagram.changelog.flatMap((entry) => entry.changes);

    // Filter by type
    if (filterType !== 'all') {
      changes = getChangelogByType(changes, filterType as any);
    }

    // Filter by user
    if (filterUser !== 'all') {
      changes = changes.filter((change) => change.user === filterUser);
    }

    // Filter by date
    if (filterDays > 0) {
       
      const cutoff = Date.now() - filterDays * 24 * 60 * 60 * 1000;
      changes = changes.filter((change) => change.timestamp >= cutoff);
    }

    return changes.sort((a, b) => b.timestamp - a.timestamp);
  }, [diagram.changelog, filterType, filterUser, filterDays]);

  // Get unique users
  const uniqueUsers = Array.from(
    new Set(
      diagram.changelog.flatMap((entry) => [
        entry.author,
        ...entry.changes.map((c) => c.user),
      ]).filter((user): user is string => Boolean(user))
    )
  ).sort();

  // Calculate stats
  const allChanges = diagram.changelog.flatMap((entry) => entry.changes);
  const totalChanges = allChanges.length;
  const changesByType = {
    'element-added': getChangelogByType(allChanges, 'element-added').length,
    'element-removed': getChangelogByType(allChanges, 'element-removed').length,
    'element-modified': getChangelogByType(allChanges, 'element-modified').length,
    'relationship-added': getChangelogByType(allChanges, 'relationship-added').length,
  };

  const recentChanges = getChangelogSince(diagram, 7).length;

  const handleExportMarkdown = () => {
    const markdown = generateChangelogMarkdown(diagram.changelog);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-changelog.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = exportChangelogToCSV(diagram);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-changelog.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getChangeIcon = (type: string) => {
    switch (type) {
      case 'created':
        return <div className="w-2 h-2 bg-green-500 rounded-full" />;
      case 'updated':
        return <div className="w-2 h-2 bg-blue-500 rounded-full" />;
      case 'deleted':
        return <div className="w-2 h-2 bg-red-500 rounded-full" />;
      case 'metadata-updated':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full" />;
      default:
        return <div className="w-2 h-2 bg-gray-500 rounded-full" />;
    }
  };

  const getChangeColor = (type: string) => {
    switch (type) {
      case 'created':
        return 'text-green-400';
      case 'updated':
        return 'text-blue-400';
      case 'deleted':
        return 'text-red-400';
      case 'metadata-updated':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
     
    const now = Date.now();
    const diff = now - timestamp;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const renderTimeline = () => {
    if (filteredEntries.length === 0) {
      return (
        <div className="p-8 text-center text-foreground-tertiary text-sm">
          No changes found matching the current filters
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {filteredEntries.map((entry) => {
          const element = diagram.elements.find((el) => el.id === entry.elementId);
          const isSelected = selectedEntry?.id === entry.id;

          return (
            <div
              key={entry.id}
              onClick={() => setSelectedEntry(isSelected ? null : entry)}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'bg-accent-muted border-accent'
                  : 'bg-cream/5 border-border hover:bg-cream/10'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-1.5">{getChangeIcon(entry.type)}</div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-sm font-medium capitalize ${getChangeColor(
                            entry.type
                          )}`}
                        >
                          {entry.type.replace(/-/g, ' ')}
                        </span>
                        {element && (
                          <>
                            <span className="text-foreground-muted">•</span>
                            <span
                              className="text-sm text-foreground hover:text-accent transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (element) onSelectElement(element.id);
                              }}
                            >
                              {element.name}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="text-xs text-foreground-tertiary flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(entry.timestamp)}
                        {entry.user && (
                          <>
                            <span className="text-foreground-muted">•</span>
                            <User className="w-3 h-3" />
                            {entry.user}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {entry.message && (
                    <div className="text-sm text-foreground-secondary mb-2">{entry.message}</div>
                  )}

                  {isSelected && (entry.before || entry.after) && (
                    <div className="mt-3 p-3 bg-background/50 rounded space-y-2">
                      <div className="text-xs font-medium text-foreground-secondary mb-2">Details</div>
                      {entry.before && (
                        <div className="text-xs">
                          <span className="text-foreground-tertiary">Before:</span>
                          <div className="text-foreground-secondary mt-1 ml-2 font-mono text-xs max-h-32 overflow-auto">
                            {typeof entry.before === 'object' ? JSON.stringify(entry.before, null, 2) : String(entry.before)}
                          </div>
                        </div>
                      )}
                      {entry.after && (
                        <div className="text-xs">
                          <span className="text-foreground-tertiary">After:</span>
                          <div className="text-foreground-secondary mt-1 ml-2 font-mono text-xs max-h-32 overflow-auto">
                            {typeof entry.after === 'object' ? JSON.stringify(entry.after, null, 2) : String(entry.after)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isSelected && entry.impactedTeams && entry.impactedTeams.length > 0 && (
                    <div className="mt-3 p-3 bg-background/50 rounded">
                      <div className="text-xs font-medium text-foreground-secondary mb-2">Impacted Teams</div>
                      <div className="flex flex-wrap gap-1">
                        {entry.impactedTeams.map((team) => (
                          <span key={team} className="text-xs px-2 py-1 bg-accent-muted text-accent rounded">
                            {team}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className="space-y-6">
        {/* Change Type Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Tag className="w-4 h-4 text-accent" />
            Change Type
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
          >
            <option value="all">All Types</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
            <option value="metadata-updated">Metadata Updated</option>
          </select>
        </div>

        {/* User Filter */}
        {uniqueUsers.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <User className="w-4 h-4 text-accent" />
              User
            </label>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            >
              <option value="all">All Users</option>
              {uniqueUsers.map((user) => (
                <option key={user} value={user}>
                  {user}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Time Range Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" />
            Time Range
          </label>
          <select
            value={filterDays}
            onChange={(e) => setFilterDays(Number(e.target.value))}
            className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
          >
            <option value={0}>All Time</option>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {/* Filter Summary */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <div className="text-sm text-foreground-secondary mb-2">
            Showing <span className="font-medium text-accent">{filteredEntries.length}</span> of{' '}
            {totalChanges} changes
          </div>
          {(filterType !== 'all' || filterUser !== 'all' || filterDays > 0) && (
            <button
              onClick={() => {
                setFilterType('all');
                setFilterUser('all');
                setFilterDays(30);
              }}
              className="text-xs text-accent hover:text-accent transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderStats = () => {
    return (
      <div className="space-y-4">
        {/* Overview */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 bg-cream/5 rounded-lg">
            <div className="text-sm text-foreground-tertiary mb-1">Total Changes</div>
            <div className="text-3xl font-bold text-foreground">{totalChanges}</div>
          </div>
          <div className="p-4 bg-cream/5 rounded-lg">
            <div className="text-sm text-foreground-tertiary mb-1">Last 7 Days</div>
            <div className="text-3xl font-bold text-foreground">{recentChanges}</div>
          </div>
        </div>

        {/* By Type */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">By Type</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-cream/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-sm text-foreground-secondary">Elements Added</span>
              </div>
              <span className="text-sm font-medium text-foreground">{changesByType['element-added']}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-cream/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                <span className="text-sm text-foreground-secondary">Elements Modified</span>
              </div>
              <span className="text-sm font-medium text-foreground">{changesByType['element-modified']}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-cream/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                <span className="text-sm text-foreground-secondary">Relationships Added</span>
              </div>
              <span className="text-sm font-medium text-foreground">{changesByType['relationship-added']}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-cream/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span className="text-sm text-foreground-secondary">Elements Removed</span>
              </div>
              <span className="text-sm font-medium text-foreground">{changesByType['element-removed']}</span>
            </div>
          </div>
        </div>

        {/* By User */}
        {uniqueUsers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">By User</h4>
            <div className="space-y-2">
              {uniqueUsers.map((user) => {
                const userChanges = getChangelogByUser(diagram, user).length;
                return (
                  <div
                    key={user}
                    className="flex items-center justify-between p-3 bg-cream/5 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-accent" />
                      <span className="text-sm text-foreground-secondary">{user}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground">{userChanges}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Activity Timeline */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Recent Activity</h4>
          <div className="space-y-2">
            {[1, 7, 30, 90].map((days) => {
              const since = Date.now() - days * 24 * 60 * 60 * 1000;
              const count = getChangelogSince(diagram, since).length;
              return (
                <div
                  key={days}
                  className="flex items-center justify-between p-3 bg-cream/5 rounded-lg"
                >
                  <span className="text-sm text-foreground-secondary">Last {days} day{days !== 1 ? 's' : ''}</span>
                  <span className="text-sm font-medium text-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <History className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Change History</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="changelog"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <button
          onClick={() => setViewMode('timeline')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'timeline'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Timeline
        </button>
        <button
          onClick={() => setViewMode('filters')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            viewMode === 'filters'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
        </button>
        <button
          onClick={() => setViewMode('stats')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'stats'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Statistics
        </button>

        {/* Export Actions */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleExportMarkdown}
            className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
            title="Export as Markdown"
          >
            <FileText className="w-4 h-4 text-foreground" />
          </button>
          <button
            onClick={handleExportCSV}
            className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
            title="Export as CSV"
          >
            <Download className="w-4 h-4 text-foreground" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {viewMode === 'timeline' && renderTimeline()}
        {viewMode === 'filters' && renderFilters()}
        {viewMode === 'stats' && renderStats()}
      </div>

      {/* Footer */}
      {viewMode === 'timeline' && filteredEntries.length > 0 && (
        <div className="p-4 border-t border-border text-xs text-foreground-tertiary text-center">
          Showing {filteredEntries.length} change{filteredEntries.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
